import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  IconAlertTriangle,
  IconBan,
  IconBell,
  IconBellRinging,
  IconBrandWhatsapp,
  IconCamera,
  IconCash,
  IconCashOff,
  IconCircleCheck,
  IconClock,
  IconDeviceDesktop,
  IconFileText,
  IconHelp,
  IconHourglassEmpty,
  IconLock,
  IconLogout,
  IconMail,
  IconMoon,
  IconPhone,
  IconPhoneCall,
  IconRotate2,
  IconSun,
  IconTrash,
  IconTrophy,
  IconUser,
  IconUserCheck,
  IconUserMinus,
  IconUserPlus,
  IconUserX,
} from "@tabler/icons-react";
import { AGENT_UPLOAD_MAX_BYTES, AgentNotificationType, AgentStatus } from "@safiri/shared";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useNotices } from "../lib/notices";
import { useTheme, type ThemePref } from "../lib/theme";
import type { AgentNotice, Me, Paged, Session } from "../lib/types";
import { formatDate, formatDateTime, phoneDigits, timeAgo, tzs } from "../lib/format";
import { SUPPORT_EMAIL, SUPPORT_PHONE } from "../config/env";
import { useI18n, type Lang, type TKey } from "../i18n";
import { normalizePhone, PASSWORD_OK, prettyPhone } from "./AuthPages";
import { InstallRow, PushSettings } from "../components/Install";
import { ChannelSwitch, ResendRow, SendCodeButton, useOtpChannel, type OtpChannel } from "../components/Otp";
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  CardHeader,
  CodeInput,
  cx,
  EmptyState,
  ErrorAlert,
  Input,
  ListRow,
  ListSkeleton,
  PageTitle,
  PasswordInput,
  PhoneInput,
  SectionTitle,
  Segmented,
  Sheet,
  Spinner,
  useAction,
  useCooldown,
  useLoad,
  useToast,
} from "../components/ui";

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

const NOTICE_ICON: Partial<Record<AgentNotificationType, { icon: typeof IconBell; cls: string }>> = {
  [AgentNotificationType.APPLICATION_RECEIVED]: { icon: IconHourglassEmpty, cls: "bg-info-soft text-info" },
  [AgentNotificationType.APPLICATION_APPROVED]: { icon: IconUserCheck, cls: "bg-success-soft text-success" },
  [AgentNotificationType.APPLICATION_REJECTED]: { icon: IconUserX, cls: "bg-danger-soft text-danger" },
  [AgentNotificationType.REFERRAL_SIGNED_UP]: { icon: IconUserPlus, cls: "bg-info-soft text-info" },
  [AgentNotificationType.REFERRAL_ACTIVE]: { icon: IconUserCheck, cls: "bg-success-soft text-success" },
  [AgentNotificationType.TIER_PAID]: { icon: IconTrophy, cls: "bg-success-soft text-success" },
  [AgentNotificationType.DEADLINE_SOON]: { icon: IconClock, cls: "bg-warning-soft text-warning" },
  [AgentNotificationType.TIER_EXPIRED]: { icon: IconHourglassEmpty, cls: "bg-sunken text-muted" },
  [AgentNotificationType.REFERRAL_DISABLED]: { icon: IconBan, cls: "bg-danger-soft text-danger" },
  [AgentNotificationType.REWARD_REVERSED]: { icon: IconRotate2, cls: "bg-danger-soft text-danger" },
  [AgentNotificationType.REFERRED_DRIVER_DELETED]: { icon: IconUserMinus, cls: "bg-sunken text-muted" },
  [AgentNotificationType.CASHOUT_SUCCEEDED]: { icon: IconCash, cls: "bg-success-soft text-success" },
  [AgentNotificationType.CASHOUT_FAILED]: { icon: IconCashOff, cls: "bg-danger-soft text-danger" },
  [AgentNotificationType.ACCOUNT_SUSPENDED]: { icon: IconAlertTriangle, cls: "bg-danger-soft text-danger" },
  [AgentNotificationType.ACCOUNT_REACTIVATED]: { icon: IconCircleCheck, cls: "bg-success-soft text-success" },
};

/** Where tapping a notification goes. */
function noticeLink(n: AgentNotice, isAgent: boolean): string | null {
  if (n.data?.referralId) return `/referrals/${n.data.referralId}`;
  if (n.type === AgentNotificationType.CASHOUT_SUCCEEDED || n.type === AgentNotificationType.CASHOUT_FAILED || n.type === AgentNotificationType.REWARD_REVERSED) return "/wallet";
  if (n.type === AgentNotificationType.APPLICATION_APPROVED && isAgent) return "/code";
  if (n.type === AgentNotificationType.APPLICATION_REJECTED || n.type === AgentNotificationType.APPLICATION_RECEIVED) return isAgent ? null : "/application";
  return null;
}

const NOTICE_PAGE = 30;

export function NotificationsPage() {
  const { t, pick, lang } = useI18n();
  const { isAgent } = useAuth();
  const navigate = useNavigate();
  const notices = useNotices();
  const toast = useToast();
  const [items, setItems] = useState<AgentNotice[]>([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [unread, setUnread] = useState(0);
  const first = useLoad(() => api.get<Paged<AgentNotice> & { unread: number }>("/agent/notifications", { page: 1, limit: NOTICE_PAGE }));

  useEffect(() => {
    if (!first.data) return;
    setItems(first.data.items);
    setPage(1);
    setPages(first.data.pagination.pages);
    setUnread(first.data.unread);
    notices.setUnread(first.data.unread);
    // Only on a fresh first page; `notices` is a stable context value.
  }, [first.data]);

  const more = useAction(async () => {
    const r = await api.get<Paged<AgentNotice>>("/agent/notifications", { page: page + 1, limit: NOTICE_PAGE });
    setItems((x) => [...x, ...r.items]);
    setPage(page + 1);
    setPages(r.pagination.pages);
  });
  const markAll = useAction(async () => {
    await api.post("/agent/notifications/read", {});
    const now = new Date().toISOString();
    setItems((x) => x.map((n) => ({ ...n, readAt: n.readAt ?? now })));
    setUnread(0);
    notices.setUnread(0);
    toast(t("allRead"));
  });
  const open = async (n: AgentNotice) => {
    if (!n.readAt) {
      const now = new Date().toISOString();
      setItems((x) => x.map((i) => (i._id === n._id ? { ...i, readAt: now } : i)));
      setUnread((u) => Math.max(0, u - 1));
      notices.setUnread(Math.max(0, unread - 1));
      void api.post("/agent/notifications/read", { ids: [n._id] }).catch(() => undefined);
    }
    const to = noticeLink(n, isAgent);
    if (to) navigate(to);
  };

  return (
    <>
      <PageTitle
        subtitle={unread > 0 ? t("unreadCount", { n: unread }) : undefined}
        action={
          unread > 0 && (
            <Button variant="ghost" size="sm" loading={markAll.loading} onClick={() => void markAll.run()}>
              {t("markAllRead")}
            </Button>
          )
        }
      >
        {t("notifications")}
      </PageTitle>
      <div className="mb-4">
        <PushSettings compact />
      </div>
      {first.error && !first.data ? (
        <ErrorAlert error={first.error} onRetry={first.reload} />
      ) : !first.data ? (
        <ListSkeleton rows={5} />
      ) : !items.length ? (
        <EmptyState icon={IconBell} title={t("noNotifications")} body={t("noNotificationsBody")} />
      ) : (
        <>
          <ul className="overflow-hidden rounded-2xl border border-line bg-surface shadow-card">
            {items.map((n) => {
              const meta = NOTICE_ICON[n.type as AgentNotificationType] ?? { icon: IconBell, cls: "bg-sunken text-ink" };
              const to = noticeLink(n, isAgent);
              return (
                <li key={n._id} className="border-b border-line last:border-0">
                  <button type="button" onClick={() => void open(n)} className={cx("flex w-full items-start gap-3 p-4 text-left transition hover:bg-sunken", !n.readAt && "bg-primary-soft/40")}>
                    <span className={cx("flex h-10 w-10 shrink-0 items-center justify-center rounded-full", meta.cls)}>
                      <meta.icon size={20} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-start justify-between gap-2">
                        <span className={cx("text-sm text-ink", n.readAt ? "font-medium" : "font-semibold")}>{pick(n.title)}</span>
                        <time className="shrink-0 text-xs text-subtle" dateTime={n.createdAt} title={formatDateTime(n.createdAt, lang)}>
                          {timeAgo(n.createdAt, lang)}
                        </time>
                      </span>
                      <span className="mt-0.5 block text-sm leading-snug text-muted">{pick(n.body)}</span>
                      {to && <span className="mt-1.5 inline-block text-xs font-semibold text-brand">{t("view")} →</span>}
                    </span>
                    {!n.readAt && <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-primary" aria-label={t("unread")} />}
                  </button>
                </li>
              );
            })}
          </ul>
          {page < pages && (
            <div className="mt-4 flex justify-center">
              <Button variant="secondary" loading={more.loading} onClick={() => void more.run()}>
                {t("loadMore")}
              </Button>
            </div>
          )}
          <ErrorAlert error={more.error} />
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Profile
// ---------------------------------------------------------------------------

function PhoneChangeSheet({ open, onClose, current }: { open: boolean; onClose: () => void; current: string }) {
  const { t } = useI18n();
  const { refresh } = useAuth();
  const toast = useToast();
  const [next, setNext] = useState("");
  const [oldPin, setOldPin] = useState<string | null>(null);
  const [newPin, setNewPin] = useState<string | null>(null);
  const [oldOtp, setOldOtp] = useState("");
  const [newOtp, setNewOtp] = useState("");
  const cooldown = useCooldown(60);
  const phone = normalizePhone(next);
  const reset = () => {
    setNext("");
    setOldPin(null);
    setNewPin(null);
    setOldOtp("");
    setNewOtp("");
  };
  const [channel, setChannel] = useOtpChannel();
  const send = useAction(async (ch: OtpChannel = channel) => {
    setChannel(ch);
    const [a, b] = await Promise.all([
      api.post<{ pinId: string }>("/agent/me/phone/otp", { target: "old", channel: ch }),
      api.post<{ pinId: string }>("/agent/me/phone/otp", { target: "new", newPhoneNumber: phone, channel: ch }),
    ]);
    setOldPin(a.pinId);
    setNewPin(b.pinId);
    cooldown.start();
  });
  const change = useAction(async () => {
    await api.post("/agent/me/phone", { newPhoneNumber: phone, oldPinId: oldPin, oldOtp: oldOtp.trim(), newPinId: newPin, newOtp: newOtp.trim() });
    await refresh();
    toast(t("phoneChanged"));
    reset();
    onClose();
  });
  const sent = Boolean(oldPin && newPin);
  return (
    <Sheet
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title={t("changePhone")}
      footer={
        sent ? (
          <Button block size="lg" loading={change.loading} disabled={oldOtp.length < 4 || newOtp.length < 4} onClick={() => void change.run()}>
            {t("changePhone")}
          </Button>
        ) : (
          <div className="flex flex-col gap-2">
            <SendCodeButton channel={channel} loading={send.loading} disabled={!/^\+255\d{9}$/.test(phone) || phone === current} onClick={() => void send.run()} />
            <ChannelSwitch channel={channel} onChange={setChannel} />
          </div>
        )
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted">{t("changePhoneBody")}</p>
        <div className="rounded-xl bg-sunken p-3 text-sm">
          <span className="text-muted">{t("currentPhone")}: </span>
          <span className="font-medium text-ink">{prettyPhone(current)}</span>
        </div>
        <PhoneInput label={t("newPhone")} value={next} disabled={sent} onChange={(e) => setNext(e.target.value)} />
        {sent && (
          <>
            <CodeInput label={t("codeToOld")} value={oldOtp} onChange={(e) => setOldOtp(e.target.value)} />
            <CodeInput label={t("codeToNew")} value={newOtp} onChange={(e) => setNewOtp(e.target.value)} />
            <p className="text-xs text-muted">{channel === "whatsapp" ? t("codesSentWhatsApp") : t("codesSentSms")}</p>
            <ResendRow channel={channel} cooldownLeft={cooldown.left} loading={send.loading} onResend={(ch) => void send.run(ch)} />
          </>
        )}
        <ErrorAlert error={send.error ?? change.error} />
      </div>
    </Sheet>
  );
}

function DeleteAccountSheet({ open, onClose, applicant }: { open: boolean; onClose: () => void; applicant: boolean }) {
  const { t } = useI18n();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const check = useLoad(() => (open ? api.get<{ allowed: boolean; reason: string | null; forfeit: number }>("/agent/me/deletion-check") : Promise.resolve(null)), [open]);
  const [password, setPassword] = useState("");
  const [sure, setSure] = useState(false);
  const del = useAction(async () => {
    await api.post("/agent/me/delete", { password, confirmForfeit: (check.data?.forfeit ?? 0) > 0 });
    await signOut();
    navigate("/login");
  });
  const c = check.data;
  const close = () => {
    setPassword("");
    setSure(false);
    onClose();
  };
  return (
    <Sheet
      open={open}
      onClose={close}
      title={applicant ? t("deleteApplication") : t("deleteAccount")}
      footer={
        c?.allowed ? (
          <Button variant="danger" block size="lg" loading={del.loading} disabled={!password || !sure} onClick={() => void del.run()}>
            <IconTrash size={18} />
            {t("confirmDelete")}
          </Button>
        ) : (
          <Button variant="secondary" block onClick={close}>
            {t("close")}
          </Button>
        )
      }
    >
      {check.loading && !c ? (
        <Spinner inline />
      ) : (
        <div className="flex flex-col gap-4">
          <Alert tone="error">{t("deleteBody")}</Alert>
          {c && !c.allowed && (
            <Alert tone="warning">
              {t(c.reason === "under-review" ? "delUnderReview" : c.reason === "payout-in-progress" ? "delPayout" : c.reason === "negative-balance" ? "delNegative" : "cashOutFirst")}
            </Alert>
          )}
          {c && !c.allowed && c.reason !== "under-review" && c.reason !== "payout-in-progress" && c.reason !== "negative-balance" && (
            <Link to="/wallet" onClick={close} className="text-sm font-semibold text-brand underline">
              {t("goToWallet")}
            </Link>
          )}
          <ErrorAlert error={check.error} onRetry={check.reload} />
          {c?.allowed && (
            <>
              {c.forfeit > 0 && <Alert tone="warning">{t("forfeitWarning", { amount: tzs(c.forfeit) })}</Alert>}
              <PasswordInput label={t("confirmWithPassword")} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
              <label className="flex items-start gap-3 text-sm text-ink">
                <input type="checkbox" checked={sure} onChange={(e) => setSure(e.target.checked)} className="mt-0.5 h-5 w-5 accent-[rgb(var(--danger))]" />
                {t("deleteUnderstand")}
              </label>
              <ErrorAlert error={del.error} />
            </>
          )}
        </div>
      )}
    </Sheet>
  );
}

const STATUS_KEY: Record<string, TKey> = {
  [AgentStatus.DRAFT]: "as_draft",
  [AgentStatus.UNDER_REVIEW]: "as_underReview",
  [AgentStatus.REJECTED]: "as_rejected",
  [AgentStatus.APPROVED]: "as_approved",
  [AgentStatus.SUSPENDED]: "as_suspended",
};
const STATUS_TONE: Record<string, string> = {
  [AgentStatus.DRAFT]: "draft",
  [AgentStatus.UNDER_REVIEW]: "underReview",
  [AgentStatus.REJECTED]: "rejected",
  [AgentStatus.APPROVED]: "approved",
  [AgentStatus.SUSPENDED]: "suspended",
};

function ThemePicker() {
  const { t } = useI18n();
  const { pref, setPref } = useTheme();
  return (
    <Segmented<ThemePref>
      label={t("appearance")}
      value={pref}
      onChange={setPref}
      options={[
        { value: "system", label: t("themeSystem"), icon: IconDeviceDesktop },
        { value: "light", label: t("themeLight"), icon: IconSun },
        { value: "dark", label: t("themeDark"), icon: IconMoon },
      ]}
    />
  );
}

/** Spec §5.5: what an agent can change themselves. */
export function ProfilePage() {
  const { t, setLang, lang } = useI18n();
  const { me, refresh, startSession } = useAuth();
  const toast = useToast();
  const [f, setF] = useState({ email: "", altPhoneNumber: "", street: "" });
  const [pw, setPw] = useState({ current: "", next: "" });
  const [photoTooBig, setPhotoTooBig] = useState(false);
  const [sheet, setSheet] = useState<"phone" | "delete" | null>(null);
  const photoRef = useRef<HTMLInputElement>(null);

  const initial = useMemo(() => ({ email: me?.email ?? "", altPhoneNumber: me?.altPhoneNumber ?? "", street: me?.street ?? "" }), [me]);
  useEffect(() => {
    setF(initial);
  }, [initial]);
  const dirty = JSON.stringify(f) !== JSON.stringify(initial);

  const save = useAction(async () => {
    const body: Record<string, unknown> = {};
    body.altPhoneNumber = f.altPhoneNumber.trim() ? normalizePhone(f.altPhoneNumber) : null;
    if (f.street.trim()) body.street = f.street.trim();
    // Optional: blank clears it. Saved straight away, no verification link.
    const nextEmail = f.email.trim().toLowerCase() || null;
    if (nextEmail !== (me?.email ?? null)) body.email = nextEmail;
    await api.patch<Me>("/agent/me", body);
    await refresh();
    toast(t("saved"));
  });
  const language = useAction(async (l: Lang) => {
    setLang(l);
    await api.patch<Me>("/agent/me", { language: l });
    await refresh();
  });
  const photo = useAction(async (file: File) => {
    await api.upload("/agent/me/uploads/profilePhoto", file);
    await refresh();
    toast(t("photoUpdated"));
  });
  const changePw = useAction(async () => {
    const s = await api.post<Session>("/agent/me/password", { currentPassword: pw.current, newPassword: pw.next });
    await startSession(s);
    setPw({ current: "", next: "" });
    toast(t("passwordChanged"));
  });

  if (!me) return <Spinner />;
  const underReview = me.status === AgentStatus.UNDER_REVIEW;
  const applicant = me.status === AgentStatus.DRAFT || me.status === AgentStatus.REJECTED;
  const canPhoto = !underReview;

  return (
    <div className="flex flex-col gap-5">
      <PageTitle>{t("profile")}</PageTitle>

      <Card>
        <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:text-left">
          <div className="relative">
            <Avatar name={me.name} src={me.profilePhotoUrl} size={88} />
            {canPhoto && (
              <button
                type="button"
                onClick={() => photoRef.current?.click()}
                disabled={photo.loading}
                className="absolute -bottom-1 -right-1 inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary text-white shadow-raised ring-4 ring-surface transition active:scale-95"
                aria-label={`${t("replace")}: ${t("profilePhoto")}`}
              >
                {photo.loading ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" /> : <IconCamera size={18} />}
              </button>
            )}
            <input
              ref={photoRef}
              type="file"
              accept="image/jpeg,image/png"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (!file) return;
                setPhotoTooBig(file.size > AGENT_UPLOAD_MAX_BYTES);
                if (file.size <= AGENT_UPLOAD_MAX_BYTES) void photo.run(file);
              }}
            />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xl font-semibold text-ink">{me.name}</p>
            <p className="mt-0.5 text-sm text-muted">{prettyPhone(me.phoneNumber)}</p>
            <div className="mt-2 flex flex-wrap justify-center gap-1.5 sm:justify-start">
              <Badge status={STATUS_TONE[me.status]}>{t(STATUS_KEY[me.status] ?? "as_draft")}</Badge>
              {me.referralCode && <Badge tone="gray">{me.referralCode}</Badge>}
            </div>
            {me.approvedAt && <p className="mt-2 text-xs text-subtle">{t("agentSince", { date: formatDate(me.approvedAt, lang) })}</p>}
          </div>
        </div>
        {photoTooBig && (
          <div className="mt-4">
            <Alert tone="error">{t("fileTooBig")}</Alert>
          </div>
        )}
        <div className="mt-4">
          <ErrorAlert error={photo.error} />
        </div>
        <dl className="mt-4 grid gap-3 border-t border-line pt-4 text-sm sm:grid-cols-3">
          <div>
            <dt className="text-xs text-subtle">{t("fullName")}</dt>
            <dd className="mt-0.5 flex items-center gap-1 font-medium text-ink">
              {me.name} <IconLock size={13} className="text-subtle" />
            </dd>
          </div>
          <div>
            <dt className="text-xs text-subtle">{t("dob")}</dt>
            <dd className="mt-0.5 flex items-center gap-1 font-medium text-ink">
              {me.dob ?? "—"} <IconLock size={13} className="text-subtle" />
            </dd>
          </div>
          <div>
            <dt className="text-xs text-subtle">{t("idDocument")}</dt>
            <dd className="mt-0.5 flex items-center gap-1 font-medium text-ink">
              {me.hasIdDocument ? t("uploaded") : "—"} <IconLock size={13} className="text-subtle" />
            </dd>
          </div>
        </dl>
        <p className="mt-3 text-xs text-subtle">
          {t("lockedFields")}{" "}
          <Link to="/help" className="font-medium text-brand">
            {t("contactSupport")}
          </Link>
        </p>
      </Card>

      <section>
        <SectionTitle>{t("contactDetails")}</SectionTitle>
        <Card>
          {underReview && (
            <div className="mb-4">
              <Alert tone="info">{t("editAfterReview")}</Alert>
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Input
                label={t("email")}
                optional
                type="email"
                inputMode="email"
                value={f.email}
                disabled={underReview}
                onChange={(e) => setF({ ...f, email: e.target.value })}
                hint={t("emailOptionalHint")}
              />
            </div>
            <PhoneInput label={t("altPhone")} value={f.altPhoneNumber} disabled={underReview} onChange={(e) => setF({ ...f, altPhoneNumber: e.target.value })} optional />
            <Input label={t("street")} value={f.street} disabled={underReview} onChange={(e) => setF({ ...f, street: e.target.value })} maxLength={200} />
          </div>
          <div className="mt-4 flex flex-col gap-3">
            <ErrorAlert error={save.error} />
            {!underReview && (
              <div className="flex gap-2">
                <Button loading={save.loading} disabled={!dirty} onClick={() => void save.run()}>
                  {t("saveChanges")}
                </Button>
                {dirty && (
                  <Button variant="ghost" onClick={() => setF(initial)}>
                    {t("cancel")}
                  </Button>
                )}
              </div>
            )}
          </div>
        </Card>
      </section>

      <section>
        <SectionTitle>{t("preferences")}</SectionTitle>
        <Card className="flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium text-ink">{t("language")}</p>
            <Segmented<Lang>
              label={t("language")}
              value={lang}
              onChange={(l) => void language.run(l)}
              options={[
                { value: "sw", label: t("swahili") },
                { value: "en", label: t("english") },
              ]}
            />
            <p className="text-xs text-subtle">{t("languageHint")}</p>
            <ErrorAlert error={language.error} />
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium text-ink">{t("appearance")}</p>
            <ThemePicker />
          </div>
          <div className="flex flex-col gap-2 border-t border-line pt-5">
            <p className="text-sm font-medium text-ink">{t("pushTitle")}</p>
            <PushSettings />
          </div>
        </Card>
      </section>

      <section>
        <SectionTitle>{t("security")}</SectionTitle>
        <Card>
          <CardHeader icon={IconLock} title={t("changePassword")} subtitle={t("changePasswordBody")} />
          <div className="grid gap-4 sm:grid-cols-2">
            <PasswordInput label={t("currentPassword")} autoComplete="current-password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} />
            <PasswordInput label={t("newPassword")} hint={t("passwordHint")} autoComplete="new-password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} error={pw.next && !PASSWORD_OK(pw.next) ? t("passwordHint") : null} />
          </div>
          <div className="mt-4 flex flex-col gap-3">
            <ErrorAlert error={changePw.error} />
            <Button className="self-start" variant="secondary" loading={changePw.loading} disabled={!pw.current || !PASSWORD_OK(pw.next)} onClick={() => void changePw.run()}>
              {t("changePassword")}
            </Button>
          </div>
        </Card>
        {me.status === AgentStatus.APPROVED && (
          <Card className="mt-3 !p-2">
            <ListRow icon={IconPhone} title={t("changePhone")} subtitle={prettyPhone(me.phoneNumber)} onClick={() => setSheet("phone")} />
          </Card>
        )}
      </section>

      <section>
        <SectionTitle>{t("dangerZone")}</SectionTitle>
        <Card className="!p-2">
          <ListRow icon={IconTrash} danger title={applicant ? t("deleteApplication") : t("deleteAccount")} subtitle={t("deleteShort")} onClick={() => setSheet("delete")} />
        </Card>
      </section>

      <PhoneChangeSheet open={sheet === "phone"} onClose={() => setSheet(null)} current={me.phoneNumber} />
      <DeleteAccountSheet open={sheet === "delete"} onClose={() => setSheet(null)} applicant={applicant} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Help
// ---------------------------------------------------------------------------

function Faq({ q, children }: { q: string; children: ReactNode }) {
  return (
    <details className="group border-b border-line last:border-0">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 py-4 font-medium text-ink [&::-webkit-details-marker]:hidden">
        {q}
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sunken text-muted transition group-open:rotate-45">+</span>
      </summary>
      <div className="pb-4 text-sm leading-relaxed text-muted">{children}</div>
    </details>
  );
}

export function HelpPage() {
  const { t } = useI18n();
  const config = useLoad(() => api.get<{ agentMinCashout: number }>("/agent/public/config"));
  const min = config.data ? tzs(config.data.agentMinCashout) : "TZS 5,000";
  const phone = SUPPORT_PHONE.replace(/\s/g, "");
  return (
    <div className="flex flex-col gap-5">
      <PageTitle subtitle={t("helpBody")}>{t("help")}</PageTitle>

      <div className="grid gap-3 sm:grid-cols-3">
        {phone && (
          <a href={`tel:${phone}`} className="flex items-center gap-3 rounded-2xl border border-line bg-surface p-4 shadow-card transition hover:border-line-strong sm:flex-col sm:items-start">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-soft text-brand">
              <IconPhoneCall size={20} />
            </span>
            <span>
              <span className="block font-semibold text-ink">{t("callUs")}</span>
              <span className="block text-sm text-muted">{SUPPORT_PHONE}</span>
            </span>
          </a>
        )}
        {phone && (
          <a href={`https://wa.me/${phoneDigits(phone)}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-2xl border border-line bg-surface p-4 shadow-card transition hover:border-line-strong sm:flex-col sm:items-start">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#25D366]/15 text-[#128C7E] dark:text-whatsapp">
              <IconBrandWhatsapp size={20} />
            </span>
            <span>
              <span className="block font-semibold text-ink">WhatsApp</span>
              <span className="block text-sm text-muted">{t("chatWithUs")}</span>
            </span>
          </a>
        )}
        <a href={`mailto:${SUPPORT_EMAIL}`} className="flex items-center gap-3 rounded-2xl border border-line bg-surface p-4 shadow-card transition hover:border-line-strong sm:flex-col sm:items-start">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-info-soft text-info">
            <IconMail size={20} />
          </span>
          <span className="min-w-0">
            <span className="block font-semibold text-ink">{t("emailUs")}</span>
            <span className="block truncate text-sm text-muted">{SUPPORT_EMAIL}</span>
          </span>
        </a>
      </div>

      <section>
        <SectionTitle>{t("faqTitle")}</SectionTitle>
        <Card className="!py-1">
          <Faq q={t("faq1q")}>{t("faq1a")}</Faq>
          <Faq q={t("faq2q")}>{t("faq2a")}</Faq>
          <Faq q={t("faq3q")}>{t("faq3a")}</Faq>
          <Faq q={t("faq4q")}>{t("faq4a", { min })}</Faq>
          <Faq q={t("faq5q")}>{t("faq5a")}</Faq>
          <Faq q={t("faq6q")}>{t("faq6a")}</Faq>
          <Faq q={t("faq7q")}>{t("faq7a")}</Faq>
        </Card>
      </section>

      <Card className="!p-2">
        <ListRow icon={IconFileText} title={t("terms")} to="/terms" />
        <InstallRow />
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// More (phones): what the sidebar shows on desktop
// ---------------------------------------------------------------------------

export function MorePage() {
  const { t } = useI18n();
  const { me, signOut } = useAuth();
  const { unread } = useNotices();
  const navigate = useNavigate();
  return (
    <div className="flex flex-col gap-5">
      <Link to="/profile" className="flex items-center gap-4 rounded-2xl border border-line bg-surface p-4 shadow-card">
        <Avatar name={me?.name ?? ""} src={me?.profilePhotoUrl} size={56} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-lg font-semibold text-ink">{me?.name}</span>
          <span className="block text-sm text-muted">{me ? prettyPhone(me.phoneNumber) : ""}</span>
          {me?.referralCode && <span className="mt-1 inline-block rounded-md bg-sunken px-2 py-0.5 font-mono text-xs tracking-wider text-ink">{me.referralCode}</span>}
        </span>
        <IconUser size={20} className="text-subtle" />
      </Link>

      <section>
        <SectionTitle>{t("account")}</SectionTitle>
        <Card className="!p-2">
          <ListRow
            icon={IconBellRinging}
            title={t("navNotifications")}
            to="/notifications"
            trailing={unread > 0 ? <span className="tabular rounded-full bg-primary px-2 py-0.5 text-xs font-bold text-white">{unread}</span> : undefined}
          />
          <ListRow icon={IconUser} title={t("navProfile")} subtitle={t("profileShort")} to="/profile" />
        </Card>
      </section>

      <section>
        <SectionTitle>{t("appearance")}</SectionTitle>
        <Card>
          <ThemePicker />
        </Card>
      </section>

      <section>
        <SectionTitle>{t("support")}</SectionTitle>
        <Card className="!p-2">
          <ListRow icon={IconHelp} title={t("navHelp")} subtitle={t("helpShort")} to="/help" />
          <ListRow icon={IconFileText} title={t("terms")} to="/terms" />
          <InstallRow />
        </Card>
      </section>

      <Card className="!p-2">
        <ListRow
          icon={IconLogout}
          danger
          title={t("signOut")}
          onClick={async () => {
            await signOut();
            navigate("/login");
          }}
        />
      </Card>
      <p className="text-center text-xs text-subtle">Smart Safiri Agents · {t("madeInTz")}</p>
    </div>
  );
}

